import { useState, type FormEvent, type MouseEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { auth as authApi } from '@/api/endpoints';
import { useAuth } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';
import { Tag } from '@/components/ui/tag';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

// Mirrors the @Size(min, max) constraint on ChangePasswordRequest.newPassword in the backend.
const PASSWORD_MIN_LENGTH = 10;
const PASSWORD_MAX_LENGTH = 128;

const successBannerClass =
  'rounded-control bg-[var(--color-success-bg)] px-3 py-2 text-sm text-[var(--color-success)]';

export function ProfilePage() {
  const { t } = useTranslation();
  // ProfilePage only ever renders behind RequireAuth, so user is always set here.
  const { user, updateUser, logout } = useAuth();

  const [firstName, setFirstName] = useState(user!.firstName ?? '');
  const [lastName, setLastName] = useState(user!.lastName ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState(false);

  const updateProfile = useMutation({
    mutationFn: () => authApi.updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() }),
    onSuccess: (updated) => {
      updateUser(updated);
      // Re-sync the form fields to the trimmed values that were actually submitted (and
      // persisted), so the inputs don't keep showing untrimmed whitespace the header no
      // longer reflects.
      setFirstName((f) => f.trim());
      setLastName((l) => l.trim());
      setNameSuccess(true);
    },
    onError: (err) => {
      setNameError(getApiErrorMessage(err, t('errors.generic')));
    },
  });

  const onSubmitName = (e: FormEvent) => {
    e.preventDefault();
    // Guards against a submit event reaching the handler while a request is already
    // in flight (e.g. a submit fired programmatically rather than via the — disabled
    // at that point — submit button), which would otherwise fire a second, redundant
    // request while the first is still pending.
    if (updateProfile.isPending) return;
    setNameError(null);
    setNameSuccess(false);
    if (!firstName.trim() || !lastName.trim()) return;
    updateProfile.mutate();
  };

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err) => {
      // The backend reports a wrong current password as a 401; surface that as an
      // inline field error instead of the generic form-level banner.
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setCurrentPasswordError(t('profile.currentPasswordIncorrect'));
      } else {
        setPasswordError(getApiErrorMessage(err, t('errors.generic')));
      }
    },
  });

  const onSubmitPassword = (e: FormEvent) => {
    e.preventDefault();
    // See the equivalent guard in onSubmitName: prevents a second, concurrent
    // change-password request (and the confusing "current password incorrect"
    // response that can come back from it after the first request already
    // succeeded and cleared the fields) while one is already in flight.
    if (changePassword.isPending) return;
    setPasswordError(null);
    setCurrentPasswordError(null);
    setPasswordSuccess(false);
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) return;
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(t('profile.passwordTooShort', { min: PASSWORD_MIN_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('profile.passwordMismatch'));
      return;
    }
    changePassword.mutate();
  };

  const nameDisabled = !firstName.trim() || !lastName.trim() || updateProfile.isPending;
  const passwordDisabled =
    !currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim() || changePassword.isPending;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  const deleteAccount = useMutation({
    mutationFn: () => authApi.deleteAccount({ password: deletePassword }),
    // The account row is gone server-side, so there is nothing left to be
    // "logged out" of on this client beyond clearing the local token —
    // RequireAuth then redirects to /login on its own.
    onSuccess: () => {
      logout();
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setDeletePasswordError(t('profile.deleteAccountWrongPassword'));
      } else {
        setDeletePasswordError(getApiErrorMessage(err, t('errors.generic')));
      }
    },
  });

  const onDeleteDialogOpenChange = (open: boolean) => {
    setDeleteDialogOpen(open);
    if (!open) {
      setDeletePassword('');
      setDeletePasswordError(null);
    }
  };

  const onConfirmDelete = (e: MouseEvent) => {
    // Keeps the dialog open on a rejected password instead of Radix's default
    // close-on-click; the button's own `disabled` prop already rules out a
    // blank password or an in-flight request reaching this at all.
    e.preventDefault();
    deleteAccount.mutate();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('profile.title')}</h1>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.personalInfo')}</CardTitle>
          <CardDescription>{t('profile.personalInfoDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmitName} className="space-y-4" aria-label="profile-name-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('auth.firstName')}</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  maxLength={64}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('auth.lastName')}</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  maxLength={64}
                  required
                />
              </div>
            </div>
            {nameError && <div role="alert" className="text-sm text-destructive">{nameError}</div>}
            {nameSuccess && (
              <div role="status" aria-live="polite" className={successBannerClass}>
                {t('profile.nameUpdated')}
              </div>
            )}
            <Button type="submit" className="w-full" loading={updateProfile.isPending} disabled={nameDisabled}>
              {t('profile.saveChanges')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.premium.title')}</CardTitle>
          <CardDescription>
            {user!.premium ? t('profile.premium.activeDesc') : t('profile.premium.inactiveDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user!.premium ? (
            <Tag tone="warning" dot>
              {t('profile.premium.badgeActive')}
            </Tag>
          ) : (
            <Tag tone="neutral" dot>
              {t('profile.premium.badgeInactive')}
            </Tag>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.password')}</CardTitle>
          <CardDescription>{t('profile.passwordDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmitPassword} className="space-y-4" aria-label="profile-password-form">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">{t('profile.currentPassword')}</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                aria-invalid={!!currentPasswordError}
                aria-describedby={currentPasswordError ? 'currentPassword-error' : undefined}
                required
              />
              {currentPasswordError && (
                <div id="currentPassword-error" className="text-sm text-destructive">
                  {currentPasswordError}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('profile.newPassword')}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                required
              />
              <p className="text-sm text-muted-foreground">
                {t('profile.newPasswordHint', { min: PASSWORD_MIN_LENGTH })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">{t('profile.confirmNewPassword')}</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                required
              />
            </div>
            {passwordError && <div role="alert" className="text-sm text-destructive">{passwordError}</div>}
            {passwordSuccess && (
              <div role="status" aria-live="polite" className={successBannerClass}>
                {t('profile.passwordUpdated')}
              </div>
            )}
            <Button type="submit" loading={changePassword.isPending} disabled={passwordDisabled}>
              {t('profile.updatePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.legal')}</CardTitle>
          <CardDescription>{t('profile.legalDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/privacy" intent="inline">
                {t('profile.legalPrivacyLink')}
              </Link>
            </li>
            <li>
              <Link to="/terms" intent="inline">
                {t('profile.legalTermsLink')}
              </Link>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle>{t('profile.dangerZone')}</CardTitle>
          <CardDescription>{t('profile.dangerZoneDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog open={deleteDialogOpen} onOpenChange={onDeleteDialogOpenChange}>
            <AlertDialogTrigger asChild>
              <Button variant="danger">{t('profile.deleteAccount')}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('profile.deleteAccountConfirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription>{t('profile.deleteAccountConfirmDesc')}</AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <Label htmlFor="deleteAccountPassword">{t('profile.deleteAccountPasswordLabel')}</Label>
                <Input
                  id="deleteAccountPassword"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    setDeletePasswordError(null);
                  }}
                  autoComplete="current-password"
                  aria-invalid={!!deletePasswordError}
                  aria-describedby={deletePasswordError ? 'deleteAccountPassword-error' : undefined}
                />
                {deletePasswordError && (
                  <div id="deleteAccountPassword-error" role="alert" className="text-sm text-destructive">
                    {deletePasswordError}
                  </div>
                )}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel asChild>
                  <Button variant="secondary">{t('common.cancel')}</Button>
                </AlertDialogCancel>
                <AlertDialogAction asChild>
                  <Button
                    variant="danger"
                    onClick={onConfirmDelete}
                    loading={deleteAccount.isPending}
                    disabled={!deletePassword.trim() || deleteAccount.isPending}
                  >
                    {t('profile.deleteAccountConfirmButton')}
                  </Button>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
